import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import FaqSection from './components/FaqSection';

const TITLE = "First Week in the UK: Checklist for Indians (2026)";
const DESCRIPTION = "Just arrived in the UK from India? Your first-week checklist: eVisa, bank account, National Insurance number, GP registration, SIM, travel and council tax.";

const steps = [
    {
        title: "Check your eVisa and UKVI account",
        body: "The UK now uses eVisas instead of BRP cards. Sign in to your UKVI account, make sure your current passport is linked, and learn how to generate a share code — landlords and employers use it to check your right to rent and work.",
    },
    {
        title: "Open a UK bank account",
        body: "You will need a UK account for your salary, rent and bills. App-based banks are often quickest to open, while high-street banks may ask for proof of address. Students can usually ask their university for a bank letter confirming their address.",
    },
    {
        title: "Apply for a National Insurance number",
        body: "You need a National Insurance (NI) number to work and pay tax correctly. Apply online on GOV.UK. You can start working before it arrives, as long as you can prove your right to work.",
    },
    {
        title: "Register with a GP (local doctor)",
        body: "Registering with a GP is free and gives you access to NHS care. Find a practice near your home on the NHS website. You do not need proof of address or immigration status to register, and your Immigration Health Surcharge already covers NHS treatment.",
    },
    {
        title: "Get a UK SIM card",
        body: "A UK number makes it much easier to open accounts, receive delivery texts and apply for jobs. Pay-as-you-go and SIM-only plans are cheap and don't need a credit history. Keep your Indian number active on WhatsApp for family back home.",
    },
    {
        title: "Sort out travel",
        body: "In London you can tap in with a contactless bank card or an Oyster card on the Tube, buses and trains. Full-time students in London can apply for an 18+ Student Oyster photocard for 30% off travelcards and bus passes. For rail travel across the UK, a 16-25 or 26-30 Railcard saves a third on most fares.",
    },
    {
        title: "Understand council tax",
        body: "Council tax is a local charge on your home. Households made up only of full-time students don't pay it, and someone living alone gets a 25% discount. Check with your landlord whether it is included in your rent, and apply for any exemption with your local council.",
    },
    {
        title: "Save the key phone numbers",
        body: "Call 999 in an emergency, 111 for urgent but non-emergency medical advice from the NHS, and 101 to contact the police when it is not an emergency.",
    },
    {
        title: "Know about the TV licence",
        body: "You need a TV licence to watch live TV on any channel or to use BBC iPlayer, on any device. If you only use services like Netflix or YouTube, you don't need one.",
    },
];

function FirstWeekUK() {
    return (
        <>
            <Helmet>
                <title>{TITLE}</title>
                <meta name="description" content={DESCRIPTION} />
                <meta property="og:title" content={TITLE} />
                <meta property="og:description" content={DESCRIPTION} />
                <meta name="twitter:title" content={TITLE} />
                <meta name="twitter:description" content={DESCRIPTION} />
            </Helmet>

            <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md my-6">
                <div className="max-w-4xl mx-auto text-left">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-indigo-700 dark:text-indigo-300 mb-4">
                        Your First Week in the UK: A Checklist for Indians
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
                        Landing in the UK is exciting, but the first few days are full of admin. Work through this checklist in your first week and you will have your paperwork, money, health care and travel sorted — so you can focus on your studies, your new job and settling in.
                    </p>

                    <ol className="space-y-5">
                        {steps.map((step, i) => (
                            <li key={step.title} className="flex gap-4">
                                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center">{i + 1}</span>
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-1">{step.title}</h2>
                                    <p className="text-gray-600 dark:text-gray-400">{step.body}</p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div className="mt-10 p-5 rounded-lg bg-indigo-50 dark:bg-gray-700/50">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">Still looking for somewhere to live?</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-3">
                            Browse rooms and flatmates from the Indian community across London, or see which areas suit you best.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/" className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold">Browse rooms</Link>
                            <Link to="/indian-areas-in-london" className="px-4 py-2 rounded-md bg-white dark:bg-gray-800 text-indigo-700 dark:text-indigo-300 text-sm font-semibold border border-indigo-200 dark:border-gray-600">Best areas for Indians</Link>
                        </div>
                    </div>

                    <FaqSection faqs={[
                        { q: "Do I need to register with a GP in the UK?", a: "It is not compulsory, but you should. Registering is free, gives you access to NHS care and is much easier to do before you get ill. You don't need proof of address or immigration status to register." },
                        { q: "Do international students pay council tax?", a: "Not if everyone in the home is a full-time student — student-only households are exempt. If you share with people who are not students, the bill may still apply, so ask your landlord and your local council." },
                        { q: "Can I start work before I get my National Insurance number?", a: "Yes. You can start work as long as you can prove your right to work in the UK, usually with an eVisa share code. Apply for your National Insurance number on GOV.UK and give it to your employer when it arrives." },
                    ]} />
                </div>
            </div>
        </>
    );
}

export default FirstWeekUK;
